/**
 * Tên khách đi từ Zalo vào hồ sơ — MỘT nguồn cho Mini App (gửi lên) và máy chủ (kiểm + lưu).
 *
 * Vì sao có: Mini App không xin `scope.userInfo` nên Zalo trả tên rỗng → "Khách Zalo", và máy chủ chỉ ghi
 * tên khi tạo hồ sơ mới (B1, 10/10/2026). Luật chung ở đây để hai phía không lệch nhau: chuẩn hoá NFC, bỏ
 * mọi ký tự điều khiển / định dạng / đảo chiều chữ / vô hình (U+202E làm bảng ca của quầy đọc ngược, Hangul
 * filler làm tên "trống" mà vẫn qua được kiểm rỗng), gọn khoảng trắng, cắt ở 60 ký tự.
 *
 * Máy chủ KHÔNG từ chối tên bẩn bằng 400 (khách tưởng hỏng rồi đặt thêm): tên được làm sạch rồi dùng; rỗng thì
 * rơi về "Khách Zalo". Riêng đường quầy gõ tên rỗng vẫn 400 (nhân viên gõ lại được).
 *
 * Regex Unicode (`\p{…}`) KHÔNG tạo lúc nạp module: Mini App dựng đích es2015 chạy trên WebView cũ — `\p{…}` là
 * lỗi cú pháp / lỗi dựng RegExp ở đó và sẽ làm sập cả gói ngay khi mở app. Tạo lười trong hàm, bọc try/catch,
 * rơi về bộ dải mã liệt kê tay (`boRegexDuPhong`) khi môi trường không hiểu `\p{…}`.
 */

/** Tên tạm khi chưa biết khách là ai. Hồ sơ mang đúng tên này là "chưa ai đặt tên" — được phép thay. */
export const TEN_KHACH_ZALO = 'Khách Zalo'

/** Tên tạm của hồ sơ "Khách vào" (quầy không gõ tên) — `repo/khach-vao.ts`. Cũng là "chưa ai đặt tên" khi gộp hồ sơ. */
export const TEN_KHACH_VANG_LAI = 'Khách vãng lai'

/** Tên hồ sơ sau khi bị gộp vào hồ sơ khác (ẩn danh như ADR-080; `customer_name_not_blank` cấm tên rỗng). ADR-091. */
export const TEN_HO_SO_DA_GOP = 'Hồ sơ đã gộp'

/** Giới hạn ở biên máy chủ (zod) — tên Zalo thường ngắn hơn nhiều. */
export const TEN_KHACH_TOI_DA = 60

/** Bộ regex dùng để làm sạch tên (hai bản: đầy đủ `\p{…}` và dự phòng dải mã liệt kê tay). */
export interface BoRegexTen {
  /** Có ký tự lạ (không cờ `g`: dùng cho `.test`). */
  kyTuLa: RegExp
  /** Ký tự lạ, cờ `g`: dùng cho `.replace`. */
  kyTuLaToan: RegExp
  /** Có ít nhất một chữ / chữ số. */
  coChu: RegExp
  /** Chuỗi 3 dấu kết hợp liền nhau trở lên (zalgo): người Việt không có tên nào cần quá 2 dấu trên một chữ. */
  dauChong: RegExp
}

/**
 * Ký tự không được nằm trong tên, theo LỚP Unicode (không liệt kê từng mã): điều khiển (`Cc`: gồm NUL,
 * xuống dòng, tab), định dạng (`Cf`: ZWJ/ZWNJ, đảo chiều chữ, word joiner, soft hyphen, BOM, tag), phân cách
 * dòng / đoạn (`Zl`, `Zp`), vùng dùng riêng (`Co`), nửa cặp thay thế lẻ (`Cs`) — cộng các ký tự "trông như
 * trống" mà Unicode xếp là chữ / ký hiệu: Hangul filler (U+115F, U+1160, U+3164, U+FFA0), ô braille trống
 * (U+2800), CGJ (U+034F), dấu phân tách nguyên âm Mông Cổ (U+180E), ký tự chú thích xen dòng (U+FFF9–FFFB).
 */
export function boRegexDayDu(): BoRegexTen {
  const nguon =
    '\\p{Cc}\\p{Cf}\\p{Zl}\\p{Zp}\\p{Co}\\p{Cs}' +
    '\\u115F\\u1160\\u3164\\uFFA0\\u2800\\u061C\\u2060-\\u2064\\u206A-\\u206F\\u00AD\\u034F\\u180E\\uFFF9-\\uFFFB' +
    '\\u{E0001}-\\u{E007F}'
  return {
    kyTuLa: new RegExp(`[${nguon}]`, 'u'),
    kyTuLaToan: new RegExp(`[${nguon}]`, 'gu'),
    // `new RegExp`, KHÔNG literal `/\p{…}/u`: literal là lỗi cú pháp ngay lúc nạp file trên WebView cũ (try/catch không bắt).
    // biome-ignore lint/complexity/useRegexLiterals: cố ý — literal Unicode làm sập cả gói trên WebView cũ (es2015).
    coChu: new RegExp('[\\p{L}\\p{N}]', 'u'),
    // biome-ignore lint/complexity/useRegexLiterals: như trên.
    dauChong: new RegExp('\\p{M}{3,}', 'gu'),
  }
}

/**
 * Bản dự phòng cho WebView không hiểu `\p{…}`: dải mã liệt kê tay, KHÔNG cờ `u` (cặp thay thế viết thẳng).
 * Rộng tay hơn bản đầy đủ ở chỗ "có chữ" (các dải chữ phổ biến thay vì mọi chữ cái Unicode).
 */
export function boRegexDuPhong(): BoRegexTen {
  const dai =
    // Cc
    '\\u0000-\\u001F\\u007F-\\u009F' +
    // Cf (gồm đảo chiều chữ, ZW*, BOM, word joiner, soft hyphen, Arabic mark, ...)
    '\\u00AD\\u0600-\\u0605\\u061C\\u06DD\\u070F\\u08E2\\u180E\\u200B-\\u200F\\u202A-\\u202E' +
    '\\u2060-\\u2064\\u2066-\\u206F\\uFEFF\\uFFF9-\\uFFFB' +
    // Zl, Zp, Co (BMP)
    '\\u2028\\u2029\\uE000-\\uF8FF' +
    // trông như trống
    '\\u115F\\u1160\\u3164\\uFFA0\\u2800\\u034F'
  const astral =
    // tag (U+E0001–E007F) + vùng dùng riêng bổ sung (U+F0000–10FFFF = cao DB80–DBFF)
    '\\uDB40[\\uDC01-\\uDC7F]|[\\uDB80-\\uDBFF][\\uDC00-\\uDFFF]'
  const nguon = `[${dai}]|${astral}`
  const dau = '[\\u0300-\\u036F\\u1AB0-\\u1AFF\\u1DC0-\\u1DFF\\u20D0-\\u20FF\\uFE20-\\uFE2F]'
  return {
    kyTuLa: new RegExp(nguon),
    kyTuLaToan: new RegExp(nguon, 'g'),
    coChu: new RegExp(
      '[0-9A-Za-z\\u00C0-\\u024F\\u0370-\\u03FF\\u0400-\\u04FF\\u0590-\\u06FF\\u0900-\\u0DFF' +
        '\\u0E00-\\u0E7F\\u1E00-\\u1EFF\\u3040-\\u30FF\\u3400-\\u9FFF\\uAC00-\\uD7AF]',
    ),
    dauChong: new RegExp(`${dau}{3,}`, 'g'),
  }
}

let boDaTao: BoRegexTen | null = null

/** Bộ regex của môi trường này: đầy đủ nếu hiểu `\p{…}`, không thì dự phòng. Tạo một lần, ở lần dùng đầu. */
function bo(): BoRegexTen {
  if (boDaTao !== null) return boDaTao
  try {
    boDaTao = boRegexDayDu()
  } catch {
    boDaTao = boRegexDuPhong()
  }
  return boDaTao
}

/** Nửa cặp thay thế LẺ (không đủ cặp) → khoảng trắng: Postgres từ chối chuỗi UTF-8 hỏng. Không dùng regex. */
function boNuaCapLe(s: string): string {
  let ra = ''
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i)
    if (c >= 0xd800 && c <= 0xdbff) {
      const d = s.charCodeAt(i + 1)
      if (d >= 0xdc00 && d <= 0xdfff) {
        ra += s.charAt(i) + s.charAt(i + 1)
        i++
      } else ra += ' '
    } else if (c >= 0xdc00 && c <= 0xdfff) ra += ' '
    else ra += s.charAt(i)
  }
  return ra
}

/** So sánh tên tạm không phân biệt hoa-thường, dấu, dấu câu, khoảng trắng: "khách zalo", "Khách Zalo.", "KhachZalo". */
function laTenTam(sach: string): boolean {
  const khoa = sach
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
  // `user name`: tên giả của môi trường chạy thử của Zalo (vòng chấm UI số 0).
  return khoa === 'khachzalo' || khoa === 'username'
}

export function tenKhachCoKyTuLa(ten: string): boolean {
  const n = ten.normalize('NFC')
  return bo().kyTuLa.test(n) || boNuaCapLe(n) !== n
}

/** `lamSachTenKhach` với bộ regex chỉ định (test chạy được cả bản dự phòng). */
export function lamSachTenKhachBang(b: BoRegexTen, raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null
  const sach = boNuaCapLe(raw.normalize('NFC'))
    .replace(b.dauChong, '')
    .replace(b.kyTuLaToan, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (sach === '' || laTenTam(sach)) return null
  const kyTu = [...sach]
  const ten =
    kyTu.length <= TEN_KHACH_TOI_DA ? sach : kyTu.slice(0, TEN_KHACH_TOI_DA).join('').trim()
  return b.coChu.test(ten) ? ten : null
}

/**
 * Tên dùng được, hoặc `null` (rỗng / chỉ khoảng trắng / chỉ ký tự vô hình / không có chữ / tên giả / "Khách
 * Zalo" viết kiểu nào cũng vậy → không có tên thật). Ký tự lạ bị gỡ thành khoảng trắng; tên dài bị cắt theo KÝ TỰ
 * (không cắt đôi cặp thay thế của emoji).
 */
export function lamSachTenKhach(raw: string | null | undefined): string | null {
  return lamSachTenKhachBang(bo(), raw)
}
